import { forwardRef, useState, useCallback, type ChangeEvent } from 'react';
import { Input } from '@/components/common';

interface PhoneInputProps {
  label?: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showValidation?: boolean;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label = 'Teléfono',
      error,
      value,
      onChange,
      onBlur,
      required = false,
      placeholder = '(###) ###-####',
      name = 'telefono',
      id,
      showValidation = true,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    // Format phone number as (###) ###-####
    const formatPhone = useCallback((input: string): string => {
      // Remove all non-digits
      let digits = input.replace(/\D/g, '');

      // If 11 digits starting with 1 (US country code), strip it
      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
      }

      // Limit to 10 digits
      const limited = digits.slice(0, 10);

      // Format based on length
      if (limited.length === 0) return '';
      if (limited.length <= 3) return `(${limited}`;
      if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }, []);

    // Get raw digits from formatted value
    const getRawDigits = useCallback((formatted: string): string => {
      return formatted.replace(/\D/g, '');
    }, []);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const formatted = formatPhone(rawValue);
      onChange(getRawDigits(formatted));
    };

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
      onBlur?.();
    };

    const digits = getRawDigits(value);
    const isComplete = digits.length === 10;
    const displayValue = formatPhone(value);

    // Validation indicator
    const showIndicator = showValidation && !isFocused && value.length > 0;

    return (
      <div className="relative">
        <Input
          ref={ref}
          type="tel"
          inputMode="numeric"
          label={label}
          name={name}
          id={id}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          error={error}
          maxLength={14} // (###) ###-####
          autoComplete="tel"
        />

        {/* Validation indicator */}
        {showIndicator && (
          <div className="absolute right-3 top-[38px]">
            {isComplete ? (
              <svg
                className="w-5 h-5 text-success-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-warning-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            )}
          </div>
        )}

        {/* Character count */}
        {isFocused && (
          <p className="mt-1 text-xs text-gray-500 text-right">
            {digits.length}/10 dígitos
          </p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';
